/**
 * Unified route element components for paths that were previously duplicated across
 * AuthenticationRoutesV1/V2 and BrokerRoutesV1/V2 (e.g. /login, /referrals, /settings).
 *
 * Each component reads user type from the Redux store and renders the appropriate
 * V1 or V2 variant, enabling a single flat route tree in the data router config.
 */
import { useLayoutEffect } from 'react';
import { applyCssIfChanged } from './components/cssUtils';
import store from '@apply/adminStore';
import { RequireAdmin, RequireAuth, RequireBroker } from '../common/SharedComponents';
import { useAppContext } from '../AppContext';

import { Login } from '../login/Login';
import { ForgotPasswordPage } from '../login/ForgotPassword';
import { PasswordReset } from '../login/PasswordReset';
import { ReferralSummaryV2 } from '../brokerPortalV2/referrals/ReferralSummaryV2/ReferralSummaryV2';
import { AdminSettings } from '../user/AdminSettings';
import { BrokerSettingsV2 } from '../brokerPortalV2/broker/settings/BrokerSettingsV2';

// CSS URL imports for loaders
import oldAdminCssUrl from '../scss/app.scss?url';
import appCssUrl from '@apply-scss/app.scss?url';
import nonAuthCssUrl from '@apply-scss/applications/nonAuthenticated.scss?url';
import brokerCssUrl from '@apply-scss/applications/broker.scss?url';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useReapplyLayoutCss() {
    useLayoutEffect(() => {
        applyCssIfChanged(window.__LAYOUT_CSS__ ?? []);
    }, []);
}

function isBrokerUser() {
    const { bfiUser } = store.getState();
    return bfiUser?.isBroker && !bfiUser?.admin;
}

// ---------------------------------------------------------------------------
// CSS Loaders
// ---------------------------------------------------------------------------

export function loginCssLoader() {
    window.__LAYOUT_CSS__ = [appCssUrl, nonAuthCssUrl];
    return null;
}

export function referralsCssLoader() {
    window.__LAYOUT_CSS__ = isBrokerUser() ? [appCssUrl, brokerCssUrl] : [oldAdminCssUrl];
    return null;
}

export function settingsCssLoader() {
    window.__LAYOUT_CSS__ = isBrokerUser() ? [appCssUrl, brokerCssUrl] : [oldAdminCssUrl];
    return null;
}

// ---------------------------------------------------------------------------
// Thin wrapper for Login — injects AppContext callbacks
// ---------------------------------------------------------------------------

const LoginWithContext = () => {
    const { setAdmin, setSuper, setBroker, setUserId, updateAuthentication } = useAppContext();
    return (
        <Login
            setAdmin={setAdmin}
            setSuper={setSuper}
            setBroker={setBroker}
            setUserId={setUserId}
            updateAuthentication={updateAuthentication}
        />
    );
};

// ---------------------------------------------------------------------------
// Unified page components
// ---------------------------------------------------------------------------

export const UnifiedLoginPage = () => {
    useReapplyLayoutCss();
    return <LoginWithContext />;
};

export const UnifiedForgotPasswordPage = () => {
    useReapplyLayoutCss();
    return <ForgotPasswordPage />;
};

export const UnifiedPasswordResetPage = () => {
    useReapplyLayoutCss();
    return <PasswordReset createPassword={true} />;
};

export const UnifiedReferralsPage = () => {
    useReapplyLayoutCss();
    return (
        <RequireBroker>
            <ReferralSummaryV2 />
        </RequireBroker>
    );
};

export const UnifiedSettingsPage = () => {
    useReapplyLayoutCss();
    const { bfiUser } = store.getState();

    if (bfiUser?.admin) {
        return (
            <RequireAdmin>
                <AdminSettings />
            </RequireAdmin>
        );
    }
    return (
        <RequireAuth>
            <BrokerSettingsV2 />
        </RequireAuth>
    );
};
